import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import ChatModal from './ChatModal';

const FloatingChatWidget = () => {
  const { user, token } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Chat user configuration for group support (store + admin)
  const chatUser = {
    id: '2,1', // Store ID 2 and Admin ID 1
    name: 'Customer Support',
    type: 'group',
    isSupport: true,
    receiver_type: 'store'
  };

  // Check for unread messages (simplified - in real app, this would poll the API)
  useEffect(() => {
    if (user && token) {
      // This would typically poll for unread messages
      // For now, we'll simulate with localStorage or just show the widget
      const checkUnreadMessages = () => {
        // In a real implementation, this would call an API to check unread count
        // For demo purposes, we'll keep it at 0
        setUnreadCount(0);
      };

      checkUnreadMessages();
      // Check every 30 seconds
      const interval = setInterval(checkUnreadMessages, 30000);
      return () => clearInterval(interval);
    }
  }, [user, token]);

  const toggleChat = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setIsMinimized(false);
    }
  };

  const minimizeChat = () => {
    setIsMinimized(true);
    setIsOpen(false);
  };

  const closeChat = () => {
    setIsOpen(false);
    setIsMinimized(false);
  };

  // Don't show widget if user is not logged in
  if (!user || !token) {
    return null;
  }

  return (
    <>
      {/* Floating Chat Button */}
      <div className="floating-chat-widget">
        {isMinimized && (
          <div className="chat-minimized" onClick={() => setIsOpen(true)}>
            <div className="minimized-content">
              <i className="fas fa-comments"></i>
              <span className="minimized-text">Need Help?</span>
              {unreadCount > 0 && (
                <span className="unread-badge">{unreadCount}</span>
              )}
            </div>
            <button className="minimized-close" onClick={(e) => { e.stopPropagation(); setIsMinimized(false); }}>
              <i className="fas fa-times"></i>
            </button>
          </div>
        )}

        {!isMinimized && (
          <div className="chat-button" onClick={toggleChat}>
            <div className="chat-icon">
              <i className="fas fa-comments"></i>
              {unreadCount > 0 && (
                <span className="unread-badge">{unreadCount}</span>
              )}
            </div>
            <div className="chat-label">
              <span>Chat Support</span>
            </div>
          </div>
        )}
      </div>

      {/* Chat Modal */}
      {isOpen && (
        <ChatModal
          chatUser={chatUser}
          onClose={closeChat}
          onMinimize={minimizeChat}
        />
      )}

      <style jsx>{`
        .floating-chat-widget {
          position: fixed;
          bottom: 20px;
          right: 20px;
          z-index: 9998;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        .chat-button {
          display: flex;
          align-items: center;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          border-radius: 50px;
          padding: 12px 20px;
          cursor: pointer;
          box-shadow: 0 4px 20px rgba(102, 126, 234, 0.4);
          transition: all 0.3s ease;
          position: relative;
        }

        .chat-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 25px rgba(102, 126, 234, 0.6);
        }

        .chat-icon {
          position: relative;
          margin-right: 12px;
        }

        .chat-icon i {
          font-size: 20px;
        }

        .unread-badge {
          position: absolute;
          top: -8px;
          right: -8px;
          background: #ff4757;
          color: white;
          border-radius: 50%;
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: bold;
          border: 2px solid white;
        }

        .chat-label {
          font-weight: 500;
          font-size: 14px;
        }

        .chat-minimized {
          display: flex;
          align-items: center;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          border-radius: 25px;
          padding: 8px 16px;
          cursor: pointer;
          box-shadow: 0 4px 20px rgba(102, 126, 234, 0.4);
          transition: all 0.3s ease;
          min-width: 140px;
        }

        .chat-minimized:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 25px rgba(102, 126, 234, 0.6);
        }

        .minimized-content {
          display: flex;
          align-items: center;
          flex-grow: 1;
        }

        .minimized-content i {
          font-size: 16px;
          margin-right: 8px;
        }

        .minimized-text {
          font-size: 13px;
          font-weight: 500;
        }

        .minimized-close {
          background: none;
          border: none;
          color: white;
          cursor: pointer;
          padding: 4px;
          margin-left: 8px;
          border-radius: 50%;
          opacity: 0.8;
          transition: opacity 0.3s ease;
        }

        .minimized-close:hover {
          opacity: 1;
        }

        /* Pulse animation for new messages */
        @keyframes pulse {
          0% {
            box-shadow: 0 4px 20px rgba(102, 126, 234, 0.4);
          }
          50% {
            box-shadow: 0 4px 20px rgba(102, 126, 234, 0.4), 0 0 0 10px rgba(102, 126, 234, 0.1);
          }
          100% {
            box-shadow: 0 4px 20px rgba(102, 126, 234, 0.4);
          }
        }

        .chat-button.unread {
          animation: pulse 2s infinite;
        }

        /* Mobile responsiveness */
        @media (max-width: 576px) {
          .floating-chat-widget {
            bottom: 15px;
            right: 15px;
          }

          .chat-button {
            padding: 10px 16px;
          }

          .chat-label {
            display: none;
          }

          .chat-icon {
            margin-right: 0;
          }

          .chat-minimized {
            min-width: 120px;
            padding: 6px 12px;
          }

          .minimized-text {
            font-size: 12px;
          }
        }

        /* Hide on very small screens if needed */
        @media (max-width: 320px) {
          .chat-label {
            display: none;
          }
        }
      `}</style>
    </>
  );
};

export default FloatingChatWidget;